import React from "react";
import { CheckCircle2, Loader2, RotateCcw, X } from "lucide-react";
import { Button } from "@/components/ui";
import { cn } from "@/lib/utils";
import type { AttributeReviewPendingFooterProps } from "./AttributeReviewPendingFooter.types";

export const AttributeReviewPendingFooter: React.FC<
  AttributeReviewPendingFooterProps
> = ({
  selectedCount,
  onClearSelection,
  onApproveSession,
  approvalStatusLabel,
  approvalMessage,
  approvalRetryable = false,
  onRetryApproval,
  canSave,
  isSaving = false,
  isApprovalRunning = false,
  sidebarCollapsed,
}) => {
  return (
    <div
      data-testid="sticky-footer"
      className={cn(
        "fixed bottom-0 right-0 z-20 transition-all duration-200",
        sidebarCollapsed ? "left-16" : "left-64",
        "bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700",
        "px-8 py-4 shadow-lg",
      )}
    >
      <div className="flex w-full items-center justify-between">
        <div className="flex items-center gap-4">
          <div>
            <span className="text-sm text-slate-500 dark:text-slate-400">
              {selectedCount} mapping{selectedCount !== 1 ? "s" : ""} selected
            </span>
            {approvalStatusLabel ? (
              <p className="mt-0.5 max-w-xl truncate text-xs text-slate-500 dark:text-slate-400">
                {approvalStatusLabel}
                {approvalMessage ? `: ${approvalMessage}` : ""}
              </p>
            ) : null}
          </div>
          <Button variant="ghost" size="sm" onClick={onClearSelection}>
            <X className="w-4 h-4" />
            Clear
          </Button>
        </div>
        <div className="flex items-center gap-3">
          {approvalRetryable && onRetryApproval && !isApprovalRunning && (
            <Button
              data-testid="retry-approval-btn"
              variant="outline"
              onClick={onRetryApproval}
              disabled={isSaving}
            >
              <RotateCcw className="w-4 h-4" />
              Retry Approval
            </Button>
          )}
          <Button
            data-testid="approve-session-btn"
            onClick={onApproveSession}
            disabled={isSaving || !canSave}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle2 className="w-4 h-4 mr-2" />
            )}
            {isApprovalRunning
              ? (approvalStatusLabel ?? "Approval running")
              : "Save selected"}
          </Button>
        </div>
      </div>
    </div>
  );
};
